/**
 * SplitLayout — a dumb, reusable two-pane horizontal split with a
 * drag- and keyboard-resizable divider.
 *
 * Built for the side-by-side live-preview layout (chat | preview), but
 * deliberately layout-agnostic: it knows nothing about chat or preview,
 * only `secondary` (left) and `primary` (right) children.
 *
 * The SECONDARY (left) pane is sized to an explicit pixel WIDTH — this is
 * the chat panel, pinned to the same width as the right side panel and
 * resized by dragging the divider; the PRIMARY (right) pane flexes to
 * fill the rest. The owner holds the width state (so it can persist
 * per-game) — this component is controlled.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '../lib/cn';

export interface SplitLayoutProps {
  /**
   * Right pane — flexes to fill the remaining width (e.g. the preview).
   * When `null`/`undefined` the split is COLLAPSED: `secondary` fills the
   * full width and no divider renders. This lets the owner keep
   * `secondary` mounted in the same slot across open/close (no remount)
   * while the primary pane comes and goes.
   */
  primary?: React.ReactNode;
  /** Left pane (e.g. the chat). Always mounted; sized by `secondaryWidth`. */
  secondary: React.ReactNode;
  /** Width of the SECONDARY (left) pane, in px. Clamped on every change. */
  secondaryWidth: number;
  /** Called with the new clamped secondary width as the user drags / keys. */
  onSecondaryWidthChange?: (width: number) => void;
  /** Min width (px) for the secondary pane. Default 300. */
  minSecondaryWidth?: number;
  /** Max width (px) for the secondary pane. Default 720. */
  maxSecondaryWidth?: number;
  /** Min width (px) reserved for the primary pane while dragging. Default 360. */
  minPrimaryWidth?: number;
  /** Keyboard step (px) per arrow press. Default 24. */
  keyStep?: number;
  /** Accessible label for the divider. */
  dividerLabel?: string;
  className?: string;
  'data-testid'?: string;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

export function SplitLayout({
  primary,
  secondary,
  secondaryWidth,
  onSecondaryWidthChange,
  minSecondaryWidth = 300,
  maxSecondaryWidth = 720,
  minPrimaryWidth = 360,
  keyStep = 24,
  dividerLabel = 'Resize panels',
  className,
  'data-testid': testId,
}: SplitLayoutProps): React.JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const collapsed = primary === null || primary === undefined;

  // Track the laid-out container width so the RENDER clamp (below) can
  // respect the same container-aware cap that drag/keyboard resizing do.
  // Without this, a persisted wide `secondaryWidth` on a narrower window
  // renders past `effectiveMax()` and squeezes/hides the primary pane
  // until the user nudges the divider. `0` (pre-layout / jsdom) falls back
  // to the static cap, preserving the prior render behaviour in tests.
  const [containerWidth, setContainerWidth] = useState(0);
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const measure = (): void => setContainerWidth(el.getBoundingClientRect().width);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Upper bound that also respects the container so the primary pane keeps
  // at least `minPrimaryWidth`; falls back to the static max before layout.
  const maxForWidth = useCallback(
    (width: number): number => {
      // Not laid out yet (e.g. first paint / tests): fall back to the static cap.
      if (width <= 0) return maxSecondaryWidth;
      return Math.max(minSecondaryWidth, Math.min(maxSecondaryWidth, width - minPrimaryWidth));
    },
    [minSecondaryWidth, maxSecondaryWidth, minPrimaryWidth],
  );

  // Live measurement for drag/keyboard (the divider drag never changes the
  // container size, so the measured value is accurate mid-gesture).
  const effectiveMax = useCallback(
    (): number => maxForWidth(containerRef.current?.getBoundingClientRect().width ?? 0),
    [maxForWidth],
  );

  const clamped = clamp(secondaryWidth, minSecondaryWidth, maxForWidth(containerWidth));

  // Pointer-drag: the secondary pane sits on the left, so its width is
  // the divider's x minus the container's left edge.
  const onPointerMove = useCallback(
    (clientX: number) => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.width <= 0) return;
      onSecondaryWidthChange?.(clamp(clientX - rect.left, minSecondaryWidth, effectiveMax()));
    },
    [onSecondaryWidthChange, minSecondaryWidth, effectiveMax],
  );

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!onSecondaryWidthChange) return;
      event.preventDefault();
      const handle = event.currentTarget;
      handle.setPointerCapture(event.pointerId);
      const move = (e: PointerEvent) => onPointerMove(e.clientX);
      const endDrag = (e: PointerEvent) => {
        if (handle.hasPointerCapture(e.pointerId)) {
          handle.releasePointerCapture(e.pointerId);
        }
        handle.removeEventListener('pointermove', move);
        handle.removeEventListener('pointerup', endDrag);
        handle.removeEventListener('pointercancel', endDrag);
        handle.removeEventListener('lostpointercapture', endDrag);
      };
      handle.addEventListener('pointermove', move);
      handle.addEventListener('pointerup', endDrag);
      handle.addEventListener('pointercancel', endDrag);
      handle.addEventListener('lostpointercapture', endDrag);
    },
    [onPointerMove, onSecondaryWidthChange],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!onSecondaryWidthChange) return;
      // Secondary (chat) is on the LEFT: ArrowRight grows it, ArrowLeft shrinks.
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        onSecondaryWidthChange(clamp(clamped + keyStep, minSecondaryWidth, effectiveMax()));
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        onSecondaryWidthChange(clamp(clamped - keyStep, minSecondaryWidth, effectiveMax()));
      } else if (event.key === 'Home') {
        event.preventDefault();
        onSecondaryWidthChange(minSecondaryWidth);
      } else if (event.key === 'End') {
        event.preventDefault();
        onSecondaryWidthChange(effectiveMax());
      }
    },
    [onSecondaryWidthChange, clamped, keyStep, minSecondaryWidth, effectiveMax],
  );

  return (
    <div
      ref={containerRef}
      className={cn('flex min-h-0 min-w-0 flex-1 overflow-hidden', className)}
      data-testid={testId}
    >
      {/* Secondary keeps a stable element identity across collapse/expand
          so its subtree never remounts; only its sizing changes. */}
      <div
        className="flex min-h-0 flex-col overflow-hidden"
        style={collapsed ? { flex: '1 1 0%', minWidth: 0 } : { width: clamped, flexShrink: 0 }}
      >
        {secondary}
      </div>
      {!collapsed && (
        <>
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label={dividerLabel}
            aria-valuemin={Math.round(minSecondaryWidth)}
            // The reachable max is the container-aware cap (what drag/keyboard
            // and the render clamp enforce), not the static `maxSecondaryWidth`
            // — otherwise the a11y max overstates what resize can reach on a
            // narrow window. Falls back to the static cap pre-layout.
            aria-valuemax={Math.round(maxForWidth(containerWidth))}
            aria-valuenow={Math.round(clamped)}
            tabIndex={onSecondaryWidthChange ? 0 : -1}
            onPointerDown={handlePointerDown}
            onKeyDown={handleKeyDown}
            data-testid={testId ? `${testId}-divider` : undefined}
            className={cn(
              // A thin, full-height divider LINE (not a grip icon). It reads as
              // a quiet hairline at rest and brightens on hover / brand-tints on
              // keyboard focus to signal it's draggable. Horizontal margin keeps
              // it off the chat panel and the preview.
              'group relative mx-2 w-px shrink-0 cursor-col-resize bg-white/10 outline-none transition-colors',
              'hover:bg-white/30 focus-visible:bg-crucible-accent',
              // Generous invisible hit area so the thin line is easy to grab.
              'before:absolute before:-inset-x-1.5 before:inset-y-0 before:content-[""]',
            )}
          />
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">{primary}</div>
        </>
      )}
    </div>
  );
}
